import { useState, useEffect, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./NavBar.css";
import navbarLogo from "../../../Assets/Images/navbar-logo.svg";
import navbarLogoWhite from "../../../Assets/Images/navbar-logo-white.svg";
import closeIcon from "../../../Assets/Icons/close-icon.svg";
import hamburgerMenuIcon from "../../../Assets/Icons/hamburger-menu-icon.svg";
import hamburgerMenuIconWhite from "../../../Assets/Icons/hamburger-menu-icon-white.svg";
import ExpandedTech from "./ExpandedTech";
import ExpandedServices from "./ExpandedServices";

const Navbar = () => {
  const location = useLocation();

  const [openDropdown, setOpenDropdown] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  const closeDropdowns = () => {
    setOpenDropdown(null);
  };

  const handleNavClick = (e, dropdownName) => {
    e.preventDefault();
    setOpenDropdown((prev) => (prev === dropdownName ? null : dropdownName));
  };

  // Escape key support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpenDropdown(null);
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // CLOSE DROPDOWN & SIDEBAR ON ROUTE CHANGE
  useEffect(() => {
    setOpenDropdown(null);
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Click outside to close dropdowns (desktop only)
  useEffect(() => {
    if (!isMobileView && openDropdown) {
      const handleClickOutside = (e) => {
        const clickedInNav = e.target.closest(".mega-menu-nav-bar");
        if (!clickedInNav) {
          setOpenDropdown(null);
        }
      };

      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openDropdown, isMobileView]);

  // Detect mobile view
  useLayoutEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (e) => setIsMobileView(e.matches);

    setIsMobileView(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleMobileItemClick = () => {
    setIsSidebarOpen(false);
    setOpenDropdown(null);
    const formSection = document.getElementById("form");
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const toggleMobileDropdown = (type) => {
    setOpenDropdown((prev) => (prev === type ? null : type));
  };

  const handleLogoClick = () => {
    setIsSidebarOpen(false);
    setOpenDropdown(null);
  };

  const blueBackgroundRoutes = [
    "/services/software-consulting",
    "/career",
    "/contact-us",
    "/contact-us/hiring-team",
    "/contact-us/build-business",
    "/blog1",
    "/blog2",
    "/blog3",
    "/blog4",
    "/blog5",
    "/technologies",
    "/technologies/web",
    "/technologies/mobile",
    "/technologies/ui-ux",
    "/technologies/cloud",
    "/technologies/database",
  ];

  const isBlueBackground = blueBackgroundRoutes.includes(location.pathname);

  // Helper to check if current path is within a section
  const isServicesActive = location.pathname.startsWith("/services");
  const isTechActive = location.pathname.startsWith("/technologies");
  const isHomeActive = location.pathname === "/";
  const isCareersActive = location.pathname === "/career";

  return (
    <>
      {/* Diagnostic (optional, remove in prod) */}
      {/* <span
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          background: isBlueBackground ? "blue" : "gray",
          color: "white",
          padding: "4px 8px",
          zIndex: 9999,
          fontSize: "12px",
        }}
      >
        BG: {isBlueBackground ? "blue" : "default"}
      </span> */}

      <nav
        className="mega-menu-nav-bar"
        data-bg={isBlueBackground ? "blue" : "default"}
      >
        <div className="content-div">
          {/* Logo */}
          <Link to="/" className="navbar-logo-link">
            <div className="quad-theory-vertical-logo">
              <img
                src={isBlueBackground ? navbarLogoWhite : navbarLogo}
                alt="Quad Theory Logo"
                className="logo-svg"
              />
              <span className="logo-text">Quad Theory Ltd.</span>
            </div>
          </Link>

          <div className="hamburger-menu" onClick={toggleSidebar}>
            <img
              src={
                isBlueBackground ? hamburgerMenuIconWhite : hamburgerMenuIcon
              }
              alt="Open menu"
              className="hamburger-icon"
            />
          </div>

          {/* Desktop Nav */}
          {!isMobileView && (
            <div className="tabs-cta-div">
              <div className="tabs-div">
                {/* Home */}
                <div
                  className={`tabs-nav-bar ${isHomeActive ? "active-nav" : ""}`}
                >
                  <Link to="/" className="nav-link" onClick={closeDropdowns}>
                    Home
                  </Link>
                </div>

                {/* Services */}
                <div
                  className={`tabs-nav-bar services-trigger ${
                    isServicesActive ? "active-nav" : ""
                  }`}
                >
                  <a
                    href="#services"
                    className="nav-link"
                    onClick={(e) => handleNavClick(e, "services")}
                  >
                    Services
                  </a>
                </div>

                {/* Technologies */}
                <div
                  className={`tabs-nav-bar tech-trigger ${
                    isTechActive ? "active-nav" : ""
                  }`}
                >
                  <a
                    href="#technologies"
                    className="nav-link"
                    onClick={(e) => handleNavClick(e, "tech")}
                  >
                    Technologies
                  </a>
                </div>

                {/* Careers */}
                <div
                  className={`tabs-nav-bar ${
                    isCareersActive ? "active-nav" : ""
                  }`}
                >
                  <Link
                    to="/career"
                    className="nav-link"
                    onClick={closeDropdowns}
                  >
                    Career
                  </Link>
                </div>
              </div>

              <div
                className="button-div"
                onClick={() => {
                  const formSection = document.getElementById("form");
                  if (formSection) {
                    formSection.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                <button className="btn-outline">
                  <span className="btn-label outline-label">
                    SCHEDULE A MEETING
                  </span>
                </button>
                <Link to="/contact-us" className="btn-primary">
                  <span className="btn-label primary-label">CONTACT US</span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Sidebar */}
      {isMobileView && (
        <>
          <div
            className={`sidebar-overlay ${isSidebarOpen ? "active" : ""}`}
            onClick={() => setIsSidebarOpen(false)}
          />
          {isSidebarOpen && (
            <button
              className="close-sidebar-outside"
              onClick={toggleSidebar}
              aria-label="Close menu"
            >
              <img src={closeIcon} alt="Close menu" className="close-icon" />
            </button>
          )}
          <div className={`sidebar-panel ${isSidebarOpen ? "open" : ""}`}>
            <div className="sidebar-header">
              <Link
                to="/"
                className="sidebar-logo-link"
                onClick={handleLogoClick}
              >
                <div className="sidebar-logo">
                  <img
                    src={navbarLogo}
                    alt="Quad Theory Logo"
                    className="logo-svg"
                  />
                  <span className="logo-text">Quad Theory Ltd.</span>
                </div>
              </Link>
            </div>

            <div className="sidebar-menu">
              {/* Home */}
              <div className="sidebar-item">
                <Link
                  to="/"
                  className={`sidebar-link ${isHomeActive ? "active" : ""}`}
                  onClick={handleMobileItemClick}
                >
                  Home
                </Link>
              </div>

              {/* Services */}
              <div className="sidebar-item">
                <div className="sidebar-dropdown">
                  <a
                    href="#services"
                    className={`sidebar-link ${
                      isServicesActive ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMobileDropdown("services");
                    }}
                  >
                    Services
                    <span
                      className={`dropdown-arrow ${
                        openDropdown === "services" ? "open" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </a>
                  {openDropdown === "services" && (
                    <div className="submenu">
                      <Link
                        to="/services/custom-software-development"
                        className={`sidebar-link submenu-link ${
                          location.pathname ===
                          "/services/custom-software-development"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Custom Software Development
                      </Link>
                      <Link
                        to="/services/developers-for-hire"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/services/developers-for-hire"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Developers for Hire
                      </Link>
                      <Link
                        to="/services/web-development"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/services/web-development"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Web Development
                      </Link>
                      <Link
                        to="/services/mobile-app-development"
                        className={`sidebar-link submenu-link ${
                          location.pathname ===
                          "/services/mobile-app-development"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Mobile App Development
                      </Link>
                      <Link
                        to="/services/software-consulting"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/services/software-consulting"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Software Consulting
                      </Link>
                      <Link
                        to="/services/data-analytics"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/services/data-analytics"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Data Analytics
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Technologies */}
              <div className="sidebar-item">
                <div className="sidebar-dropdown">
                  <a
                    href="#technologies"
                    className={`sidebar-link ${isTechActive ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMobileDropdown("tech");
                    }}
                  >
                    Technologies
                    <span
                      className={`dropdown-arrow ${
                        openDropdown === "tech" ? "open" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </a>
                  {openDropdown === "tech" && (
                    <div className="submenu">
                      <Link
                        to="/technologies/web"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/technologies/web"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Web Technology
                      </Link>
                      <Link
                        to="/technologies/mobile"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/technologies/mobile"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Mobile Technology
                      </Link>
                      <Link
                        to="/technologies/ui-ux"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/technologies/ui-ux"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        UX&UI Design
                      </Link>
                      <Link
                        to="/technologies/cloud"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/technologies/cloud"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Cloud
                      </Link>
                      <Link
                        to="/technologies/database"
                        className={`sidebar-link submenu-link ${
                          location.pathname === "/technologies/database"
                            ? "active"
                            : ""
                        }`}
                        onClick={handleMobileItemClick}
                      >
                        Database
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Careers */}
              <div className="sidebar-item">
                <Link
                  to="/career"
                  className={`sidebar-link ${isCareersActive ? "active" : ""}`}
                  onClick={handleMobileItemClick}
                >
                  Career
                </Link>
              </div>
            </div>

            <div className="sidebar-buttons">
              <Link
                to="/contact-us"
                className="btn-primary"
                onClick={handleMobileItemClick}
              >
                <span className="btn-label primary-label">CONTACT US</span>
              </Link>
              <button className="btn-outline" onClick={handleMobileItemClick}>
                <span className="btn-label outline-label">
                  SCHEDULE A MEETING
                </span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Desktop Dropdowns — ONLY rendered on desktop */}
      {!isMobileView && openDropdown === "services" && <ExpandedServices />}
      {!isMobileView && openDropdown === "tech" && <ExpandedTech />}
    </>
  );
};

export default Navbar;
